import { useEffect, useState } from 'react';
import { Box, Typography } from '@mui/material';
import { useDispatch } from 'react-redux';

import { FHSelect } from '../../../../../../../../base';
import ErrorBox from '../../../errorBox';
import NumericInput from '../../../numericInput';
import { goalActions } from '../../../../../../../../../store';
import { FORTA_BLACK, FORTA_ERROR, FORTA_GRAY2 } from '../../../../../../../../../utils/colors';
import { BackgroundTypes, Themes } from '../../../../../../../../../constants';
import { convertToNumber } from './utils/general';
import { validateInstanceMasteryCriteria } from '../../../../../common/utils/general';
import { GoalMeasurementDurationUnit } from '../../../../constants/general';

import type {
  MasteryCriteriaChangeTypeEnum,
  TreatmentPlanResourceVersion,
} from '../../../../constants/general';
import type {
  IBRGMasterCriteria,
  ITreatmentPlanGoal,
  SelectValueType,
} from '../../../../../../../../../utils/types';

export interface ISingleRowInstanceMasteryCriteriaState {
  change_type: MasteryCriteriaChangeTypeEnum | null;
  desired_amount: number | null;
  practice_duration_count: number | null;
  practice_duration_unit: string | null;
}

interface ISingleRowInstanceMasteryCriteriaProps {
  goal: ITreatmentPlanGoal;
  goalIndex: number;
  goalVersion: TreatmentPlanResourceVersion;
  isEditable: boolean;
  isBRG?: boolean;
}

function isInstanceMasteryCriteria(
  criteria: ITreatmentPlanGoal['mastery_criteria'],
): criteria is IBRGMasterCriteria {
  return Boolean(criteria && 'practice_duration_unit' in criteria);
}

function SingleRowInstanceMasteryCriteria({
  goal,
  goalIndex,
  goalVersion,
  isBRG,
}: ISingleRowInstanceMasteryCriteriaProps) {
  const { baseline_at_creation, mastery_criteria } = goal;

  const dispatch = useDispatch();

  // We only need to support Instance data collection method in BRG goal
  const instanceMasteryCriteria = isInstanceMasteryCriteria(mastery_criteria)
    ? mastery_criteria
    : null;
  const { change_type, desired_amount, practice_duration_count, practice_duration_unit } =
    instanceMasteryCriteria ?? {};

  const [masteryCriteriaState, setMasterCriteriaState] =
    useState<ISingleRowInstanceMasteryCriteriaState>({
      change_type: change_type ?? null,
      desired_amount: desired_amount ?? null,
      practice_duration_count: practice_duration_count ?? null,
      practice_duration_unit: practice_duration_unit ?? null,
    });
  const [isDirty, setIsDirty] = useState(false);

  useEffect(() => {
    const { mastery_criteria } = goal;
    const instanceMasteryCriteria = isInstanceMasteryCriteria(mastery_criteria)
      ? mastery_criteria
      : null;
    const { change_type, desired_amount, practice_duration_count, practice_duration_unit } =
      instanceMasteryCriteria ?? {};
    if (!goal?.is_editing) {
      setIsDirty(false);
    }
    setMasterCriteriaState({
      change_type: change_type ?? null,
      desired_amount: desired_amount ?? null,
      practice_duration_count: practice_duration_count ?? null,
      practice_duration_unit: practice_duration_unit ?? null,
    });
  }, [goal?.is_editing, goal?.id]);

  const handleUpdateField = (key: string, value: string | number) => {
    setMasterCriteriaState((prev) => ({
      ...prev,
      [key]: value,
    }));
    setIsDirty(true);
  };

  useEffect(() => {
    if (!isDirty) return;
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'mastery_criteria',
        value: {
          change_type: masteryCriteriaState.change_type,
          desired_amount: convertToNumber(masteryCriteriaState.desired_amount),
          practice_duration_count: convertToNumber(masteryCriteriaState.practice_duration_count),
          practice_duration_unit: masteryCriteriaState.practice_duration_unit,
        },
      }),
    );
    dispatch(
      goalActions.updateGoalProperty({
        goalIndex,
        goalType: goal.goal_type,
        key: 'dirty',
        value: true,
      }),
    );
  }, [masteryCriteriaState]);

  const errors = validateInstanceMasteryCriteria(
    masteryCriteriaState,
    baseline_at_creation === null ? null : Number(baseline_at_creation),
    goalVersion,
  );
  const hasErrors = Object.keys(errors).length > 0;

  return (
    <Box
      sx={{
        mb: 2,
        p: 2,
        border: `1px solid ${hasErrors ? FORTA_ERROR : FORTA_GRAY2}`,
        borderRadius: '12px',
      }}
      data-testid="SingleRowInstanceMasteryCriteria"
    >
      {hasErrors && (
        <Typography textAlign="left" color={FORTA_ERROR} mt={2} mb={1}>
          {errors.desired_amount || errors.practice_duration_count || errors.baseline_at_creation}
        </Typography>
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap' }}>
        <Typography textAlign="left" display="inline">
          Client will
        </Typography>
        {goal?.is_editing ? (
          <FHSelect
            data-testid={`masterCriteriaChangeType-${goalIndex}`}
            sx={{ minWidth: '81px', mx: 1, borderRadius: '6px' }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            backgroundType={BackgroundTypes.Colored}
            options={[
              { value: 'decrease', label: 'decrease' },
              { value: 'increase', label: 'increase' },
            ]}
            value={masteryCriteriaState.change_type ? masteryCriteriaState.change_type : ''}
            onUpdate={(value: SelectValueType) => {
              handleUpdateField('change_type', value);
            }}
            error={!change_type}
          />
        ) : change_type ? (
          <Typography textAlign="left" sx={{ mx: 0.5 }}>{` ${change_type} `}</Typography>
        ) : (
          <ErrorBox width={70} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ mr: 0.5 }}>
          instances of the target skill/target behavior from
        </Typography>
        <Typography
          textAlign="left"
          display="inline"
          fontWeight={700}
          color={baseline_at_creation || baseline_at_creation === 0 ? FORTA_BLACK : FORTA_ERROR}
          sx={{ ml: 0.5 }}
        >
          {baseline_at_creation || baseline_at_creation === 0 ? baseline_at_creation : '?'}
        </Typography>

        <Typography sx={{ ml: 0.5 }}>to</Typography>
        {goal?.is_editing ? (
          <NumericInput
            testId={`masterCriteriaDesiredLevel`}
            sx={{
              width: 45,
              minWidth: 45,
              borderRadius: 1.5,
              ml: 0.5,
            }}
            value={masteryCriteriaState.desired_amount}
            onChange={(value) => {
              handleUpdateField('desired_amount', value ?? '');
            }}
            error={
              desired_amount === null ||
              desired_amount === undefined ||
              Boolean(errors.desired_amount)
            }
          />
        ) : desired_amount || desired_amount === 0 ? (
          <Typography textAlign="left" fontWeight={700} display="inline" sx={{ ml: 0.5 }}>
            {desired_amount}
          </Typography>
        ) : (
          <ErrorBox width={40} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ ml: 1 }}>
          instances per session across{' '}
        </Typography>
        {goal?.is_editing ? (
          <NumericInput
            testId={`masterCriteriaPracticeDurationCount-${goalIndex}`}
            sx={{
              width: 45,
              minWidth: 45,
              borderRadius: 1.5,
              ml: 0.5,
            }}
            value={masteryCriteriaState.practice_duration_count}
            onChange={(value) => {
              handleUpdateField('practice_duration_count', value ?? '');
            }}
            error={!practice_duration_count}
          />
        ) : practice_duration_count ? (
          <Typography textAlign="left" display="inline" fontWeight={700} sx={{ mx: 0.5 }}>
            {practice_duration_count}
          </Typography>
        ) : (
          <ErrorBox width={40} sx={{ mx: 0.5 }} />
        )}
        <Typography textAlign="left" display="inline" sx={{ ml: goal?.is_editing ? 1 : 0 }}>
          {isBRG ? 'consecutive' : 'consecutive sessions'}
        </Typography>
        {goal?.is_editing && isBRG ? (
          <FHSelect
            data-testid={`masterCriteriaDurationUnit-${goalIndex}`}
            sx={{ minWidth: '81px', mx: 1, borderRadius: '6px' }}
            inputHeight={40}
            colorTheme={Themes.Primary}
            backgroundType={BackgroundTypes.Colored}
            options={[
              {
                value: GoalMeasurementDurationUnit.Session,
                label: GoalMeasurementDurationUnit.Session,
              },
              {
                value: GoalMeasurementDurationUnit.Month,
                label: GoalMeasurementDurationUnit.Month,
              },
              {
                value: GoalMeasurementDurationUnit.Week,
                label: GoalMeasurementDurationUnit.Week,
              },
              { value: GoalMeasurementDurationUnit.Day, label: GoalMeasurementDurationUnit.Day },
            ]}
            value={
              masteryCriteriaState.practice_duration_unit
                ? masteryCriteriaState.practice_duration_unit
                : ''
            }
            onUpdate={(value: SelectValueType) => {
              handleUpdateField('practice_duration_unit', value);
            }}
            error={!practice_duration_unit}
          />
        ) : (
          <Typography display="inline" ml={0.5}>
            {practice_duration_unit}
          </Typography>
        )}
      </Box>
    </Box>
  );
}

export default SingleRowInstanceMasteryCriteria;
